// src/services/api.js
import axios from "axios";
import { toHttpError } from "./httpErrors";

/**
 * Base API URL
 * MUST point to your backend
 */
const BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  "https://jangcoin-backend.onrender.com/api"
).replace(/\/+$/, "");

/**
 * Axios instance
 */
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json"
  },
  withCredentials: true // required for refresh cookies
});

/* -------------------------------------------------------------------------- */
/* Token handling (in-memory only)                                             */
/* -------------------------------------------------------------------------- */

let accessToken = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function clearAccessToken() {
  accessToken = null;
}

export function getAccessToken() {
  return accessToken;
}

/* -------------------------------------------------------------------------- */
/* Request interceptor                                                        */
/* -------------------------------------------------------------------------- */

api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`
      };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* -------------------------------------------------------------------------- */
/* Refresh token logic (SINGLE-FLIGHT)                                         */
/* -------------------------------------------------------------------------- */

let refreshPromise = null;

async function refreshToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${BASE_URL}/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        const token = res.data?.accessToken;
        if (token) {
          setAccessToken(token);
          return token;
        }
        throw new Error("No accessToken in refresh response");
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

/* -------------------------------------------------------------------------- */
/* Response interceptor                                                       */
/* -------------------------------------------------------------------------- */

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;

    // Attempt refresh once on 401
    if (status === 401 && !error.config.__retry) {
      error.config.__retry = true;
      try {
        const newToken = await refreshToken();
        error.config.headers.Authorization = `Bearer ${newToken}`;
        return api.request(error.config);
      } catch {
        clearAccessToken();
      }
    }

    throw toHttpError(error);
  }
);

/* -------------------------------------------------------------------------- */
/* Convenience helpers                                                        */
/* -------------------------------------------------------------------------- */

export async function apiGet(url, config) {
  const res = await api.get(url, config);
  return res.data;
}

export async function apiPost(url, body, config) {
  const res = await api.post(url, body, config);
  return res.data;
}

export default api;
